import { useEffect, forwardRef, useRef } from 'react';
import { optionalProp } from '../core';
import Input, { InputTypes, MLPVersion } from '../Input/Input';
import PhoneNumber from '../PhoneNumber/PhoneNumber';
import { InputType } from './types/enums';
import { type SmartInputProps } from './types/propsType';

const SmartInput = forwardRef<HTMLInputElement, SmartInputProps>(
  (
    {
      name = 'smartInput-element',
      dropdownLabel = '',
      dropdownValue = '',
      disabledFilter = false,
      dropdownDisabled = false,
      hideDropdown = false,
      data,
      configData,
      smartInputType = InputType.EMAIL_MOBILE,
      defaultLabel = '',
      mobileLabel = '',
      emailLabel = '',
      inputValue = '',
      inputIcon,
      iconClassName,
      inputType = InputTypes.TEXT,
      inputDisabled = false,
      message,
      status,
      filterDropdownValues,
      autoLeadingZero,
      inputWithDropdown = false,
      countrySelectorWithName = false,
      applyDropdownSearch = false,
      className,
      onChange,
      onInputChange,
      ...restProps
    },
    ref,
  ) => {
    const nestedComponentId = 'nextgen-smart-input-id';

    const latestUnsanitizedValue = useRef<string>(inputValue);

    useEffect(() => {
      latestUnsanitizedValue.current = inputValue;
    }, [inputValue]);

    useEffect(() => {
      const getCurrentElement = document.querySelector(`#float-input-field-${nestedComponentId}`) as HTMLInputElement;
      if (getCurrentElement && getCurrentElement !== document.activeElement) {
        getCurrentElement.focus();
      }
    }, [inputValue]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const unsanitizedValue = event.target.value;
      let changeEvent = event;
      latestUnsanitizedValue.current = unsanitizedValue;
      if (unsanitizedValue && !/^\d*$/.test(unsanitizedValue)) {
        changeEvent = { ...event, target: { ...event.target, value: unsanitizedValue.replace(/^0+/, '') } };
      }
      onChange?.(changeEvent);
    };

    const handleInputChange = (value?: string, _status?: string) => {
      if (!onInputChange) {
        return;
      }

      const unsanitized = latestUnsanitizedValue.current;

      if (value !== unsanitized && unsanitized && !/^\d*$/.test(unsanitized)) {
        onInputChange(unsanitized.replace(/^0+/, ''));
      } else {
        onInputChange(value);
      }
    };

    const InputProps = {
      name: name,
      value: inputValue,
      disabled: inputDisabled,
      mlpVersion: inputWithDropdown ? MLPVersion.ONE : MLPVersion.TWO,
      ...{ ...optionalProp('status', status) },
      ...{ ...optionalProp('message', message) },
      ...{ ...optionalProp('inputIcon', inputIcon) },
      ...{ ...optionalProp('iconClassName', iconClassName) },
      ...{ ...optionalProp('className', className) },
      onChange: handleChange,
      onInputChange: handleInputChange,
      ...restProps,
    };

    const MobileInputField = (
      <div>
        <PhoneNumber
          name="phoneNumber-element"
          dropdownLabel={dropdownLabel}
          dropdownValue={dropdownValue}
          disabledFilter={disabledFilter}
          dropdownDisabled={dropdownDisabled}
          hideDropdown={hideDropdown || (inputValue === '' && smartInputType === InputType.EMAIL_MOBILE)}
          {...optionalProp('data', data)}
          {...optionalProp('configData', configData)}
          {...optionalProp('message', message)}
          {...optionalProp('status', status)}
          {...optionalProp('filterDropdownValues', filterDropdownValues)}
          {...optionalProp('autoLeadingZero', autoLeadingZero)}
          {...optionalProp('className', className)}
          inputLabel={smartInputType === InputType.MOBILE || inputValue !== '' ? mobileLabel : defaultLabel}
          inputType={inputType}
          inputValue={inputValue}
          inputDisabled={inputDisabled}
          onChange={handleChange}
          onInputChange={handleInputChange}
          {...restProps}
          {...(inputValue === '' && { 'data-testid': 'default-smart-input' })}
          id={nestedComponentId}
          forwardedRef={ref}
          mlpVersion={countrySelectorWithName ? MLPVersion.ONE : MLPVersion.TWO}
          inputWithDropdown={inputWithDropdown}
          applySearchFilter={applyDropdownSearch}
        />
      </div>
    );

    const EmailInputField = (
      <div>
        <Input data-testid="input-email" label={emailLabel} {...InputProps} id={nestedComponentId} forwardedRef={ref} />
      </div>
    );

    const InputField = () => {
      let inputField: React.ReactNode;
      const currentValue = latestUnsanitizedValue.current;
      if ((currentValue === '' && !countrySelectorWithName) || /^\d+$/.test(currentValue)) {
        inputField = MobileInputField;
      } else {
        inputField = EmailInputField;
      }

      return inputField;
    };

    switch (smartInputType) {
      case InputType.EMAIL:
        return EmailInputField;
      case InputType.MOBILE:
        return MobileInputField;
      case InputType.EMAIL_MOBILE:
      default:
        return <div>{InputField()}</div>;
    }
  },
);
SmartInput.displayName = 'SmartInput';

export { InputType };
export default SmartInput;
